import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import '../styles/maintenance-barrier-premium.css';

const COUNTDOWN_SECONDS = 10;

export default function MaintenanceBarrier() {
  const { user, maintenanceActive, maintenanceActivatedAt, logout } = useAuth();
  const hasSuperAdminBypass = Boolean(user?.superAdminBypass);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [startedAt, setStartedAt] = useState(null);
  const [loggedOut, setLoggedOut] = useState(false);

  useEffect(() => {
    if (!maintenanceActive) {
      setSecondsLeft(COUNTDOWN_SECONDS);
      setStartedAt(null);
      setLoggedOut(false);
      return;
    }

    if (maintenanceActivatedAt && (!startedAt || maintenanceActivatedAt > startedAt)) {
      setStartedAt(maintenanceActivatedAt);
      setSecondsLeft(COUNTDOWN_SECONDS);
      setLoggedOut(false);
    }
  }, [maintenanceActive, maintenanceActivatedAt, startedAt]);

  useEffect(() => {
    if (!maintenanceActive) return undefined;
    if (secondsLeft <= 0) return undefined;

    const t = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);

    return () => clearInterval(t);
  }, [maintenanceActive, secondsLeft]);

  useEffect(() => {
    if (!maintenanceActive) return;
    if (secondsLeft === 0 && !loggedOut) {
      setLoggedOut(true);
      (async () => {
        try {
          await logout();
        } catch (_) {
          // ignore logout errors
        }
      })();
    }
  }, [secondsLeft, maintenanceActive, loggedOut, logout]);

  if (!maintenanceActive || hasSuperAdminBypass) return null;

  const closing = secondsLeft <= 0;
  const progress = Math.round(((COUNTDOWN_SECONDS - secondsLeft) / COUNTDOWN_SECONDS) * 100);
  const countdownDisplay = String(secondsLeft).padStart(2, '0');

  return (
    <div
      className={`crm-maint-barrier${closing ? ' crm-maint-barrier--closing' : ''}`}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="crm-maint-barrier-title"
      aria-describedby="crm-maint-barrier-desc"
      aria-live="assertive"
    >
      <div className="crm-maint-barrier__card">
        <div className="crm-maint-barrier__chip">
          <span className="crm-maint-barrier__chip-dot" aria-hidden />
          Mantenimiento
        </div>

        {!closing ? (
          <div
            className="crm-maint-barrier__count-wrap"
            style={{ '--progress': progress }}
            aria-hidden
          >
            <div className="crm-maint-barrier__count-ring" />
            <div className="crm-maint-barrier__count-value">
              <span className="crm-maint-barrier__count-num">{countdownDisplay}</span>
              <span className="crm-maint-barrier__count-label">seg</span>
            </div>
          </div>
        ) : null}

        <h2 id="crm-maint-barrier-title" className="crm-maint-barrier__title">
          Aplicación en modo mantenimiento
        </h2>

        <p id="crm-maint-barrier-desc" className="crm-maint-barrier__text">
          {secondsLeft > 0 ? (
            <>
              La aplicación se pondrá en mantenimiento en{' '}
              <strong>{countdownDisplay} segundos</strong>.
            </>
          ) : (
            <>La aplicación está en mantenimiento. Volvé a intentar más tarde.</>
          )}
        </p>

        <div className="crm-maint-barrier__steps">
          <div className="crm-maint-barrier__step">
            <span className="crm-maint-barrier__step-icon" aria-hidden>
              1
            </span>
            <span>Guardamos tu sesión de forma segura antes de cerrarla.</span>
          </div>
          <div className="crm-maint-barrier__step">
            <span className="crm-maint-barrier__step-icon" aria-hidden>
              2
            </span>
            <span>El equipo está actualizando la plataforma Grupo Bienestar.</span>
          </div>
          <div className="crm-maint-barrier__step">
            <span className="crm-maint-barrier__step-icon" aria-hidden>
              3
            </span>
            <span>Cuando finalice, podrás volver a iniciar sesión con normalidad.</span>
          </div>
        </div>

        <p className="crm-maint-barrier__foot">Grupo Bienestar · CRM</p>
      </div>
    </div>
  );
}
